import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import {
  OWNER_PARAM,
  OwnerContext,
  effectiveOwner,
  parseOwnerId,
  syncOwner,
  withOwner,
  type OwnerSelection,
} from "./owner.ts";
import { usePermissions } from "./permissions.ts";

/**
 * Mantiene de acuerdo el dueno elegido y el `?owner=` de la URL.
 *
 * MANDA LA URL CUANDO TRAE EL PARAMETRO: un enlace compartido, una recarga o
 * "atras" en el navegador tienen que dejar la pantalla mirando lo que la URL
 * dice. Cuando NO lo trae —un `NavLink` del menu, la campana, un `Link` de una
 * tarjeta: ninguno sabe del dueno— se repone el elegido, con `replace` para no
 * ensuciar el historial con dos entradas por cada destino.
 *
 * CON UNA EXCEPCION: la URL que `setOwner` acaba de pedir y que el enrutador
 * todavia no ha aplicado. Entre el `setChosen` y la llegada de la URL nueva hay
 * un repintado con la URL VIEJA, y si en ese repintado mandara la URL, «Volver
 * a la mia» adoptaria de nuevo el dueno anterior y lo repondria en cuanto
 * llegara la URL limpia (BUGLOG 2026-09-12). Se recuerda la `key` de la
 * ubicacion desde la que se navego: mientras siga siendo esa, la navegacion no
 * ha llegado y la URL que se ve no cuenta. Las reglas viven en `syncOwner`, que
 * es una funcion pura con su test.
 *
 * Se monta dentro de `RequireSession`, con la sesion ya resuelta: necesita saber
 * quien es "yo" y si puede leer la de otro para no pedir nunca un dueno que el
 * servidor va a contestar con un 404 en todas las lecturas a la vez.
 */
export function OwnerProvider(props: { children: ReactNode }): ReactNode {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, canReadOthers } = usePermissions();

  const inUrl = params.has(OWNER_PARAM);
  const fromUrl = effectiveOwner(parseOwnerId(params.get(OWNER_PARAM)), user?.id, canReadOthers);

  const [chosen, setChosen] = useState<string | undefined>(fromUrl);

  /* La `key` de la ubicacion desde la que `setOwner` navego, o `null` si no hay
     ninguna navegacion propia en vuelo. Un ref y no un estado: cambiarlo no
     tiene que repintar nada. */
  const departedFrom = useRef<string | null>(null);

  useEffect(() => {
    const navigating = departedFrom.current !== null && departedFrom.current === location.key;

    if (!navigating) departedFrom.current = null;

    const sync = syncOwner({ inUrl, fromUrl, chosen, navigating });

    switch (sync.kind) {
      case "strip": {
        /* Un `?owner=` que no vale —mal formado, el propio, o de quien no puede
           mirar otras— se quita de la URL en vez de dejarlo ahi mintiendo. */
        const next = new URLSearchParams(params);

        next.delete(OWNER_PARAM);
        setParams(next, { replace: true });
        setChosen(undefined);
        break;
      }
      case "adopt":
        setChosen(sync.ownerId);
        break;
      case "restore": {
        const next = new URLSearchParams(params);

        next.set(OWNER_PARAM, sync.ownerId);
        setParams(next, { replace: true });
        break;
      }
      case "keep":
        break;
    }
  }, [inUrl, fromUrl, chosen, params, setParams, location.key]);

  const setOwner = useCallback(
    (ownerId: string | undefined, pathname?: string) => {
      const next = effectiveOwner(ownerId, user?.id, canReadOthers);

      departedFrom.current = location.key;
      setChosen(next);

      /* Al cambiar de ruta se parte de una query limpia: los filtros de la
         bitacora que se estaba mirando no son los de la pantalla a la que se va. */
      navigate(
        {
          pathname: pathname ?? location.pathname,
          search: withOwner(pathname === undefined ? location.search : "", next),
        },
        { replace: pathname === undefined },
      );
    },
    [user?.id, canReadOthers, navigate, location.key, location.pathname, location.search],
  );

  const value = useMemo<OwnerSelection>(() => ({ ownerId: chosen, setOwner }), [chosen, setOwner]);

  return <OwnerContext.Provider value={value}>{props.children}</OwnerContext.Provider>;
}
